import { 
  ArrowDownTrayIcon, 
  ChevronDownIcon,
  DocumentPlusIcon,
  XMarkIcon,
  ChartBarIcon,
  UserGroupIcon,
  CalendarIcon,
  FireIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';
import TabNavigation from '../components/TabNavigation';
import clsx from 'clsx';

interface ReportSection {
  id: string;
  name: string;
  enabled: boolean;
}

const ReportsPage: React.FC = () => {
  const { gym } = useAuth();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  
  // Custom report builder state
  const [customReportSections, setCustomReportSections] = useState<ReportSection[]>([
    { id: 'overview', name: 'Overview Metrics', enabled: true },
    { id: 'member-growth', name: 'Member Growth', enabled: true },
    { id: 'member-breakdown', name: 'Member Breakdown', enabled: true },
    { id: 'class-attendance', name: 'Class Attendance', enabled: false },
    { id: 'workout-completion', name: 'Workout Completion', enabled: false },
    { id: 'peak-hours', name: 'Peak Hours', enabled: false },
    { id: 'communication', name: 'Communication Metrics', enabled: false },
  ]);
  
  // Chart data states
  const [memberGrowth, setMemberGrowth] = useState<Array<{ date: string; count: number }>>([]);
  const [memberBreakdown, setMemberBreakdown] = useState<Array<{ type: string; count: number }>>([]);
  const [classAttendance, setClassAttendance] = useState<Array<{ date: string; attendance: number }>>([]);
  const [workoutCompletion, setWorkoutCompletion] = useState<Array<{ date: string; completions: number }>>([]);
  const [peakHours, setPeakHours] = useState<Array<{ hour: number; bookings: number }>>([]);
  const [communicationCampaigns, setCommunicationCampaigns] = useState<Array<{ date: string; count: number }>>([]);
  const [communicationEngagement, setCommunicationEngagement] = useState<{
    total_sent: number;
    total_delivered: number;
    total_failed: number;
    total_opened: number;
    total_clicked: number;
    open_rate: number;
    click_rate: number;
    delivery_rate: number;
  } | null>(null);
  const [communicationTypeBreakdown, setCommunicationTypeBreakdown] = useState<Array<{ type: string; count: number }>>([]);
  const [comprehensiveReport, setComprehensiveReport] = useState<{
    period: { start: string; end: string };
    metrics: {
      total_members: number;
      active_members: number;
      inactive_members: number;
      total_classes: number;
      total_bookings: number;
      total_workout_completions: number;
      new_members: number;
    };
  } | null>(null);
  const [revenueReport, setRevenueReport] = useState<any>(null);
  const [memberEngagement, setMemberEngagement] = useState<any>(null);
  const [memberRetention, setMemberRetention] = useState<any>(null);
  
  // Date range for comprehensive report
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  usePageTitle('Reports & Analytics');

  useEffect(() => {
    // Load only overview data initially
    loadOverviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'revenue' && !revenueReport) {
      loadRevenueData();
    } else if (activeTab === 'engagement' && !memberEngagement) {
      loadEngagementData();
    } else if (activeTab === 'members' && memberGrowth.length === 0) {
      loadMemberData();
    } else if (activeTab === 'classes' && classAttendance.length === 0) {
      loadClassData();
    } else if (activeTab === 'workouts' && workoutCompletion.length === 0) {
      loadWorkoutData();
    } else if (activeTab === 'communication' && !communicationEngagement) {
      loadCommunicationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadOverviewData = async () => {
    setIsLoading(true);
    try {
      const [comprehensiveData] = await Promise.allSettled([
        apiService.getComprehensiveReport(startDate, endDate),
      ]);
      
      if (comprehensiveData.status === 'fulfilled') setComprehensiveReport(comprehensiveData.value);
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRevenueData = async () => {
    try {
      const [revenueData] = await Promise.allSettled([
        apiService.getRevenueReport(startDate, endDate),
      ]);
      if (revenueData.status === 'fulfilled') setRevenueReport(revenueData.value);
    } catch (error) {
      console.error('Failed to load revenue data:', error);
    }
  };

  const loadEngagementData = async () => {
    try {
      const [engagementData, retentionData] = await Promise.allSettled([
        apiService.getMemberEngagement(startDate, endDate),
        apiService.getMemberRetention(),
      ]);
      if (engagementData.status === 'fulfilled') setMemberEngagement(engagementData.value);
      if (retentionData.status === 'fulfilled') setMemberRetention(retentionData.value);
    } catch (error) {
      console.error('Failed to load engagement data:', error);
    }
  };

  const loadMemberData = async () => {
    try {
      const [growthData, breakdownData] = await Promise.allSettled([
        apiService.getMemberGrowth(),
        apiService.getMemberBreakdown(),
      ]);
      if (growthData.status === 'fulfilled') setMemberGrowth(growthData.value);
      if (breakdownData.status === 'fulfilled') setMemberBreakdown(breakdownData.value);
    } catch (error) {
      console.error('Failed to load member data:', error);
    }
  };

  const loadClassData = async () => {
    try {
      const [attendanceData, hoursData] = await Promise.allSettled([
        apiService.getClassAttendance(),
        apiService.getPeakHours(),
      ]);
      if (attendanceData.status === 'fulfilled') setClassAttendance(attendanceData.value);
      if (hoursData.status === 'fulfilled') setPeakHours(hoursData.value);
    } catch (error) {
      console.error('Failed to load class data:', error);
    }
  };

  const loadWorkoutData = async () => {
    try {
      const [completionData] = await Promise.allSettled([
        apiService.getWorkoutCompletion(),
      ]);
      if (completionData.status === 'fulfilled') setWorkoutCompletion(completionData.value);
    } catch (error) {
      console.error('Failed to load workout data:', error);
    }
  };

  const loadCommunicationData = async () => {
    try {
      const [commCampaignsData, commEngagementData, commTypeData] = await Promise.allSettled([
        apiService.getCommunicationCampaignsReport(),
        apiService.getCommunicationEngagement(),
        apiService.getCommunicationTypeBreakdown(),
      ]);
      if (commCampaignsData.status === 'fulfilled') setCommunicationCampaigns(commCampaignsData.value);
      if (commEngagementData.status === 'fulfilled') setCommunicationEngagement(commEngagementData.value);
      if (commTypeData.status === 'fulfilled') setCommunicationTypeBreakdown(commTypeData.value);
    } catch (error) {
      console.error('Failed to load communication data:', error);
    }
  };

  const handleDateRangeChange = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getComprehensiveReport(startDate, endDate);
      setComprehensiveReport(data);
    } catch (error) {
      console.error('Failed to load comprehensive report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let yPos = 20;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const lineHeight = 7;
    
    // Helper function to add new page if needed
    const checkNewPage = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };
    
    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Gym Analytics Report', 105, yPos, { align: 'center' });
    yPos += 10;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, yPos, { align: 'center' });
    yPos += 8;
    
    if (gym) {
      doc.text(`Gym: ${gym.name}`, 105, yPos, { align: 'center' });
      yPos += 8;
    }
    
    if (comprehensiveReport) {
      doc.text(`Report Period: ${comprehensiveReport.period.start} to ${comprehensiveReport.period.end}`, 105, yPos, { align: 'center' });
      yPos += 10;
    }
    
    // Overview Metrics
    const enabledSections = customReportSections.filter(s => s.enabled);
    if (enabledSections.some(s => s.id === 'overview') && comprehensiveReport) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Overview Metrics', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const metrics = comprehensiveReport.metrics;
      const overviewData = [
        ['Metric', 'Value'],
        ['Total Members', metrics.total_members.toString()],
        ['Active Members', metrics.active_members.toString()],
        ['Inactive Members', metrics.inactive_members.toString()],
        ['New Members (Period)', metrics.new_members.toString()],
        ['Total Classes', metrics.total_classes.toString()],
        ['Total Bookings', metrics.total_bookings.toString()],
        ['Workout Completions', metrics.total_workout_completions.toString()],
      ];
      
      overviewData.forEach((row, index) => {
        checkNewPage(10);
        if (index === 0) {
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        doc.text(row[0], margin, yPos);
        doc.text(row[1], 150, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Member Growth
    if (enabledSections.some(s => s.id === 'member-growth') && memberGrowth.length > 0) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Member Growth', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setFont('helvetica', 'bold');
      doc.text('Date', margin, yPos);
      doc.text('New Members', 100, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      memberGrowth.slice(-30).forEach(item => {
        checkNewPage(10);
        doc.text(formatChartDate(item.date), margin, yPos);
        doc.text(item.count.toString(), 100, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Member Breakdown
    if (enabledSections.some(s => s.id === 'member-breakdown') && memberBreakdown.length > 0) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Membership Type Breakdown', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Type', margin, yPos);
      doc.text('Count', 100, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      memberBreakdown.forEach(item => {
        checkNewPage(10);
        doc.text(item.type, margin, yPos);
        doc.text(item.count.toString(), 100, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Class Attendance
    if (enabledSections.some(s => s.id === 'class-attendance') && classAttendance.length > 0) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Class Attendance Trend', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', margin, yPos);
      doc.text('Attendance', 100, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      classAttendance.slice(-30).forEach(item => {
        checkNewPage(10);
        doc.text(formatChartDate(item.date), margin, yPos);
        doc.text(item.attendance.toString(), 100, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Workout Completion
    if (enabledSections.some(s => s.id === 'workout-completion') && workoutCompletion.length > 0) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Workout Completion Trend', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', margin, yPos);
      doc.text('Completions', 100, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      workoutCompletion.slice(-30).forEach(item => {
        checkNewPage(10);
        doc.text(formatChartDate(item.date), margin, yPos);
        doc.text(item.completions.toString(), 100, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Peak Hours
    if (enabledSections.some(s => s.id === 'peak-hours') && peakHours.length > 0) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Peak Booking Hours', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Hour', margin, yPos);
      doc.text('Bookings', 100, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      peakHours.forEach(item => {
        checkNewPage(10);
        doc.text(formatHour(item.hour), margin, yPos);
        doc.text(item.bookings.toString(), 100, yPos);
        yPos += lineHeight;
      });
      yPos += 5;
    }
    
    // Communication Metrics
    if (enabledSections.some(s => s.id === 'communication') && communicationEngagement) {
      checkNewPage(30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Communication Engagement', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const commData = [
        ['Total Sent', communicationEngagement.total_sent.toString()],
        ['Total Delivered', communicationEngagement.total_delivered.toString()],
        ['Total Failed', communicationEngagement.total_failed.toString()],
        ['Total Opened', communicationEngagement.total_opened.toString()],
        ['Total Clicked', communicationEngagement.total_clicked.toString()],
        ['Delivery Rate', `${communicationEngagement.delivery_rate.toFixed(1)}%`],
        ['Open Rate', `${communicationEngagement.open_rate.toFixed(1)}%`],
        ['Click Rate', `${communicationEngagement.click_rate.toFixed(1)}%`],
      ];
      
      commData.forEach((row) => {
        checkNewPage(10);
        doc.text(row[0], margin, yPos);
        doc.text(row[1], 150, yPos);
        yPos += lineHeight;
      });
    }
    
    doc.save(`gym-report-${new Date().toISOString().split('T')[0]}.pdf`);
    setShowExportMenu(false);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const enabledSections = customReportSections.filter(s => s.enabled);
    
    // Overview Sheet
    if (enabledSections.some(s => s.id === 'overview') && comprehensiveReport) {
      const overviewData = [
        ['Gym Analytics Report'],
        [`Generated: ${new Date().toLocaleString()}`],
        gym ? [`Gym: ${gym.name}`] : [],
        [`Report Period: ${comprehensiveReport.period.start} to ${comprehensiveReport.period.end}`],
        [],
        ['Overview Metrics'],
        ['Metric', 'Value'],
        ['Total Members', comprehensiveReport.metrics.total_members],
        ['Active Members', comprehensiveReport.metrics.active_members],
        ['Inactive Members', comprehensiveReport.metrics.inactive_members],
        ['New Members (Period)', comprehensiveReport.metrics.new_members],
        ['Total Classes', comprehensiveReport.metrics.total_classes],
        ['Total Bookings', comprehensiveReport.metrics.total_bookings],
        ['Workout Completions', comprehensiveReport.metrics.total_workout_completions],
      ];
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');
    }
    
    // Member Growth Sheet
    if (enabledSections.some(s => s.id === 'member-growth') && memberGrowth.length > 0) {
      const growthData = [
        ['Date', 'New Members'],
        ...memberGrowth.map(item => [item.date, item.count]),
      ];
      const growthSheet = XLSX.utils.aoa_to_sheet(growthData);
      XLSX.utils.book_append_sheet(workbook, growthSheet, 'Member Growth');
    }
    
    // Member Breakdown Sheet
    if (enabledSections.some(s => s.id === 'member-breakdown') && memberBreakdown.length > 0) {
      const breakdownData = [
        ['Membership Type', 'Count'],
        ...memberBreakdown.map(item => [item.type, item.count]),
      ];
      const breakdownSheet = XLSX.utils.aoa_to_sheet(breakdownData);
      XLSX.utils.book_append_sheet(workbook, breakdownSheet, 'Member Breakdown');
    }
    
    // Class Attendance Sheet
    if (enabledSections.some(s => s.id === 'class-attendance') && classAttendance.length > 0) {
      const attendanceData = [
        ['Date', 'Attendance'],
        ...classAttendance.map(item => [item.date, item.attendance]),
      ];
      const attendanceSheet = XLSX.utils.aoa_to_sheet(attendanceData);
      XLSX.utils.book_append_sheet(workbook, attendanceSheet, 'Class Attendance');
    }
    
    // Workout Completion Sheet
    if (enabledSections.some(s => s.id === 'workout-completion') && workoutCompletion.length > 0) {
      const completionData = [
        ['Date', 'Completions'],
        ...workoutCompletion.map(item => [item.date, item.completions]),
      ];
      const completionSheet = XLSX.utils.aoa_to_sheet(completionData);
      XLSX.utils.book_append_sheet(workbook, completionSheet, 'Workout Completion');
    }
    
    // Peak Hours Sheet
    if (enabledSections.some(s => s.id === 'peak-hours') && peakHours.length > 0) {
      const hoursData = [
        ['Hour', 'Bookings'],
        ...peakHours.map(item => [formatHour(item.hour), item.bookings]),
      ];
      const hoursSheet = XLSX.utils.aoa_to_sheet(hoursData);
      XLSX.utils.book_append_sheet(workbook, hoursSheet, 'Peak Hours');
    }
    
    // Communication Sheet
    if (enabledSections.some(s => s.id === 'communication') && communicationEngagement) {
      const commData = [
        ['Communication Engagement Metrics'],
        [],
        ['Metric', 'Value'],
        ['Total Sent', communicationEngagement.total_sent],
        ['Total Delivered', communicationEngagement.total_delivered],
        ['Total Failed', communicationEngagement.total_failed],
        ['Total Opened', communicationEngagement.total_opened],
        ['Total Clicked', communicationEngagement.total_clicked],
        ['Delivery Rate (%)', communicationEngagement.delivery_rate],
        ['Open Rate (%)', communicationEngagement.open_rate],
        ['Click Rate (%)', communicationEngagement.click_rate],
      ];
      const commSheet = XLSX.utils.aoa_to_sheet(commData);
      XLSX.utils.book_append_sheet(workbook, commSheet, 'Communication');
    }
    
    XLSX.writeFile(workbook, `gym-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportMenu(false);
  };

  const exportToCSV = () => {
    const enabledSections = customReportSections.filter(s => s.enabled);
    let csvContent = `Gym Analytics Report\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n`;
    if (gym) {
      csvContent += `Gym: ${gym.name}\n`;
    }
    if (comprehensiveReport) {
      csvContent += `Report Period: ${comprehensiveReport.period.start} to ${comprehensiveReport.period.end}\n`;
    }
    csvContent += `\n`;
    
    // Overview
    if (enabledSections.some(s => s.id === 'overview') && comprehensiveReport) {
      csvContent += `\nOverview Metrics\n`;
      csvContent += `Metric,Value\n`;
      csvContent += `Total Members,${comprehensiveReport.metrics.total_members}\n`;
      csvContent += `Active Members,${comprehensiveReport.metrics.active_members}\n`;
      csvContent += `Inactive Members,${comprehensiveReport.metrics.inactive_members}\n`;
      csvContent += `New Members (Period),${comprehensiveReport.metrics.new_members}\n`;
      csvContent += `Total Classes,${comprehensiveReport.metrics.total_classes}\n`;
      csvContent += `Total Bookings,${comprehensiveReport.metrics.total_bookings}\n`;
      csvContent += `Workout Completions,${comprehensiveReport.metrics.total_workout_completions}\n`;
    }
    
    // Member Growth
    if (enabledSections.some(s => s.id === 'member-growth') && memberGrowth.length > 0) {
      csvContent += `\nMember Growth\n`;
      csvContent += `Date,New Members\n`;
      memberGrowth.forEach(item => {
        csvContent += `${item.date},${item.count}\n`;
      });
    }
    
    // Member Breakdown
    if (enabledSections.some(s => s.id === 'member-breakdown') && memberBreakdown.length > 0) {
      csvContent += `\nMember Breakdown\n`;
      csvContent += `Type,Count\n`;
      memberBreakdown.forEach(item => {
        csvContent += `${item.type},${item.count}\n`;
      });
    }
    
    // Class Attendance
    if (enabledSections.some(s => s.id === 'class-attendance') && classAttendance.length > 0) {
      csvContent += `\nClass Attendance\n`;
      csvContent += `Date,Attendance\n`;
      classAttendance.forEach(item => {
        csvContent += `${item.date},${item.attendance}\n`;
      });
    }
    
    // Workout Completion
    if (enabledSections.some(s => s.id === 'workout-completion') && workoutCompletion.length > 0) {
      csvContent += `\nWorkout Completion\n`;
      csvContent += `Date,Completions\n`;
      workoutCompletion.forEach(item => {
        csvContent += `${item.date},${item.completions}\n`;
      });
    }
    
    // Peak Hours
    if (enabledSections.some(s => s.id === 'peak-hours') && peakHours.length > 0) {
      csvContent += `\nPeak Hours\n`;
      csvContent += `Hour,Bookings\n`;
      peakHours.forEach(item => {
        csvContent += `${formatHour(item.hour)},${item.bookings}\n`;
      });
    }
    
    // Communication
    if (enabledSections.some(s => s.id === 'communication') && communicationEngagement) {
      csvContent += `\nCommunication Engagement\n`;
      csvContent += `Metric,Value\n`;
      csvContent += `Total Sent,${communicationEngagement.total_sent}\n`;
      csvContent += `Total Delivered,${communicationEngagement.total_delivered}\n`;
      csvContent += `Total Failed,${communicationEngagement.total_failed}\n`;
      csvContent += `Total Opened,${communicationEngagement.total_opened}\n`;
      csvContent += `Total Clicked,${communicationEngagement.total_clicked}\n`;
      csvContent += `Delivery Rate,${communicationEngagement.delivery_rate}\n`;
      csvContent += `Open Rate,${communicationEngagement.open_rate}\n`;
      csvContent += `Click Rate,${communicationEngagement.click_rate}\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gym-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  };

  const toggleReportSection = (sectionId: string) => {
    setCustomReportSections(prev => 
      prev.map(section => 
        section.id === sectionId ? { ...section, enabled: !section.enabled } : section
      )
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'revenue', name: 'Revenue', icon: CurrencyDollarIcon },
    { id: 'members', name: 'Members', icon: UserGroupIcon },
    { id: 'engagement', name: 'Engagement', icon: HeartIcon },
    { id: 'classes', name: 'Classes', icon: CalendarIcon },
    { id: 'workouts', name: 'Workouts', icon: FireIcon },
    { id: 'communication', name: 'Communication', icon: ChatBubbleLeftRightIcon },
  ];

  // Format dates for charts (show last 7 days labels)
  const formatChartDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Format hour for peak hours chart
  const formatHour = (hour: number) => {
    return `${hour}:00`;
  };

  // Format label for tooltips
  const formatTooltipLabel = (label: any) => {
    if (typeof label === 'string') {
      return formatChartDate(label);
    }
    return String(label);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className={clsx(
            "text-4xl font-bold bg-clip-text text-transparent",
            theme === 'dark'
              ? "bg-gradient-to-r from-white to-gray-300"
              : "bg-gradient-to-r from-gray-900 to-gray-700"
          )}>
            Reports & Analytics
          </h1>
          <p className={clsx(
            "mt-3 text-lg transition-colors",
            theme === 'dark' ? "text-gray-300" : "text-gray-600"
          )}>
            Track your gym's performance and generate comprehensive reports
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowReportBuilder(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <DocumentPlusIcon className="h-5 w-5" />
            <span>Create Custom Report</span>
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export</span>
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            {showExportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowExportMenu(false)}
                />
                <div className={clsx(
                  "absolute right-0 mt-2 w-48 backdrop-blur-lg rounded-xl border shadow-xl z-20 transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20"
                    : "bg-white border-gray-200"
                )}>
                  <button
                    onClick={exportToPDF}
                    className={clsx(
                      "w-full text-left px-4 py-3 rounded-t-xl transition-colors",
                      theme === 'dark'
                        ? "text-white hover:bg-white/10"
                        : "text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    Export as PDF
                  </button>
                  <button
                    onClick={exportToExcel}
                    className={clsx(
                      "w-full text-left px-4 py-3 transition-colors",
                      theme === 'dark'
                        ? "text-white hover:bg-white/10"
                        : "text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    Export as Excel
                  </button>
                  <button
                    onClick={exportToCSV}
                    className={clsx(
                      "w-full text-left px-4 py-3 rounded-b-xl transition-colors",
                      theme === 'dark'
                        ? "text-white hover:bg-white/10"
                        : "text-gray-900 hover:bg-gray-100"
                    )}
                  >
                    Export as CSV
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Report Builder Modal */}
      {showReportBuilder && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowReportBuilder(false);
            }
          }}
        >
          <div 
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/20 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/20">
              <h2 className={clsx(
                "text-2xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Create Custom Report</h2>
              <button
                onClick={() => setShowReportBuilder(false)}
                className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                )}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className={clsx(
                "mb-4 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>
                Select which sections you want to include in your custom report:
              </p>
              {customReportSections.map((section) => (
                <label
                  key={section.id}
                  className={clsx(
                    "flex items-center space-x-3 p-4 rounded-lg transition-colors cursor-pointer",
                    theme === 'dark'
                      ? "bg-white/5 hover:bg-white/10"
                      : "bg-gray-100/50 hover:bg-gray-200/50"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={section.enabled}
                    onChange={() => toggleReportSection(section.id)}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>{section.name}</span>
                </label>
              ))}
              <div className={clsx(
                "flex justify-end space-x-3 pt-4 border-t transition-colors",
                theme === 'dark' ? "border-white/20" : "border-gray-200"
              )}>
                <button
                  onClick={() => setShowReportBuilder(false)}
                  className={clsx(
                    "px-6 py-2 transition-colors",
                    theme === 'dark' ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                  )}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReportBuilder(false);
                    // Report sections are already updated via toggleReportSection
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200"
                >
                  Save Report Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <TabNavigation
        tabs={tabs.map(tab => ({
          id: tab.id,
          label: tab.name,
          icon: <tab.icon className="h-5 w-5" />
        }))}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="flex justify-center"
      />

      {/* Overview Tab */}
      {activeTab === 'overview' && comprehensiveReport && (
        <div className="space-y-6">
          {/* Date Range Selector */}
                <div className={clsx(
                  "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20"
                    : "bg-white/80 border-gray-200/50"
                )}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={clsx(
                "text-xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Report Period</h3>
              <button
                onClick={handleDateRangeChange}
                className="px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
              >
                Update
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={clsx(
                    "px-4 py-2 backdrop-blur-sm border rounded-lg transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  )}
                />
              </div>
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={clsx(
                    "px-4 py-2 backdrop-blur-sm border rounded-lg transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Total Members</h3>
              <p className={clsx(
                "text-3xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{comprehensiveReport.metrics.total_members}</p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Active Members</h3>
              <p className="text-3xl font-bold text-green-400">{comprehensiveReport.metrics.active_members}</p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Total Classes</h3>
              <p className="text-3xl font-bold text-blue-400">{comprehensiveReport.metrics.total_classes}</p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Workout Completions</h3>
              <p className="text-3xl font-bold text-purple-400">{comprehensiveReport.metrics.total_workout_completions}</p>
            </div>
          </div>

          {/* Member Growth Chart */}
          {memberGrowth.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Member Growth (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={memberGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label: any) => `Date: ${formatTooltipLabel(label)}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    name="New Members"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Member Growth (Last 30 Days)</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No member growth data available for the last 30 days.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div className="space-y-6">
          {/* Member Breakdown Chart */}
          {memberBreakdown.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Membership Type Breakdown</h3>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={memberBreakdown}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    fill="#8B5CF6"
                    label={(entry: any) => `${entry.type}: ${entry.count}`}
                  >
                    {memberBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Membership Type Breakdown</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No membership breakdown data available.</p>
              </div>
            </div>
          )}

          {/* Member Growth Chart */}
          {memberGrowth.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Daily Member Signups</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={memberGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label: any) => `Date: ${formatTooltipLabel(label)}`}
                  />
                  <Legend />
                  <Bar dataKey="count" fill="#3B82F6" name="New Members" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Daily Member Signups</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No member signup data available for the last 30 days.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && revenueReport && (
        <div className="space-y-6">
          {/* Revenue Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Total Revenue</h3>
              <p className="text-3xl font-bold text-green-400">
                ${revenueReport.summary?.total_revenue?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Net Revenue</h3>
              <p className="text-3xl font-bold text-blue-400">
                ${revenueReport.summary?.net_revenue?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Refunds</h3>
              <p className="text-3xl font-bold text-red-400">
                ${revenueReport.summary?.total_refunds?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Pending Payments</h3>
              <p className="text-3xl font-bold text-yellow-400">
                ${revenueReport.summary?.pending_payments?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>

          {/* Revenue by Day Chart */}
          {revenueReport.revenue_by_day && revenueReport.revenue_by_day.length > 0 && (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Daily Revenue</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueReport.revenue_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    formatter={(value: any) => `$${parseFloat(value).toFixed(2)}`}
                  />
                  <Bar dataKey="total" fill="#10B981" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Revenue by Membership Type */}
          {revenueReport.revenue_by_membership && revenueReport.revenue_by_membership.length > 0 && (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Revenue by Membership Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={revenueReport.revenue_by_membership}
                    dataKey="total"
                    nameKey="membership_type"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    fill="#8B5CF6"
                    label={(entry: any) => `${entry.membership_type}: $${entry.total.toFixed(2)}`}
                  >
                    {revenueReport.revenue_by_membership.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    formatter={(value: any) => `$${parseFloat(value).toFixed(2)}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Engagement Tab */}
      {activeTab === 'engagement' && memberEngagement && (
        <div className="space-y-6">
          {/* Engagement Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Active Members</h3>
              <p className="text-3xl font-bold text-green-400">
                {memberEngagement.metrics?.active_members || 0}
              </p>
              <p className={clsx(
                "text-sm mt-1 transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                {memberEngagement.metrics?.active_member_rate?.toFixed(1) || 0}% of total
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Avg Workouts/Member</h3>
              <p className="text-3xl font-bold text-blue-400">
                {memberEngagement.metrics?.avg_workouts_per_member?.toFixed(1) || 0}
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Class Attendance Rate</h3>
              <p className="text-3xl font-bold text-purple-400">
                {memberEngagement.metrics?.attendance_rate?.toFixed(1) || 0}%
              </p>
            </div>
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>Total Workouts</h3>
              <p className="text-3xl font-bold text-orange-400">
                {memberEngagement.metrics?.total_workouts || 0}
              </p>
            </div>
          </div>

          {/* Retention Metrics */}
          {memberRetention && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>New Members (30d)</h3>
                <p className="text-3xl font-bold text-green-400">
                  {memberRetention.new_members_30d || 0}
                </p>
              </div>
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Retention Rate</h3>
                <p className="text-3xl font-bold text-blue-400">
                  {memberRetention.retention_rate?.toFixed(1) || 0}%
                </p>
              </div>
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Churned Members</h3>
                <p className="text-3xl font-bold text-red-400">
                  {memberRetention.churned_members || 0}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Classes Tab */}
      {activeTab === 'classes' && (
        <div className="space-y-6">
          {/* Class Attendance Chart */}
          {classAttendance.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Class Attendance Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={classAttendance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label: any) => `Date: ${formatTooltipLabel(label)}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="attendance" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Attendance"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Class Attendance Trend</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No class attendance data available for the last 30 days.</p>
              </div>
            </div>
          )}

          {/* Peak Hours Chart */}
          {peakHours.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Peak Booking Hours</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={peakHours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={formatHour}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label) => `Hour: ${formatHour(label)}`}
                  />
                  <Legend />
                  <Bar dataKey="bookings" fill="#F59E0B" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Peak Booking Hours</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No booking data available for peak hours analysis.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Workouts Tab */}
      {activeTab === 'workouts' && (
        <div className="space-y-6">
          {/* Workout Completion Chart */}
          {workoutCompletion.length > 0 ? (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Workout Completion Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={workoutCompletion}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label: any) => `Date: ${formatTooltipLabel(label)}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="completions" 
                    stroke="#EF4444" 
                    strokeWidth={2}
                    name="Completions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={clsx(
              "backdrop-blur-lg rounded-xl border p-6 transition-colors",
              theme === 'dark'
                ? "bg-white/10 border-white/20"
                : "bg-white/80 border-gray-200/50"
            )}>
              <h3 className={clsx(
                "text-xl font-bold mb-4 transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>Workout Completion Trend</h3>
              <div className="text-center py-12">
                <p className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>No workout completion data available for the last 30 days.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Communication Tab */}
      {activeTab === 'communication' && (
        <div className="space-y-6">
          {/* Engagement Metrics */}
          {communicationEngagement && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Total Sent</h3>
                <p className={clsx(
                  "text-3xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>{communicationEngagement.total_sent}</p>
              </div>
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Delivery Rate</h3>
                <p className="text-3xl font-bold text-green-400">{communicationEngagement.delivery_rate.toFixed(1)}%</p>
              </div>
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Open Rate</h3>
                <p className="text-3xl font-bold text-blue-400">{communicationEngagement.open_rate.toFixed(1)}%</p>
              </div>
              <div className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Click Rate</h3>
                <p className="text-3xl font-bold text-purple-400">{communicationEngagement.click_rate.toFixed(1)}%</p>
              </div>
            </div>
          )}

          {/* Campaigns Over Time */}
          <div className={clsx(
            "backdrop-blur-lg rounded-xl border p-6 transition-colors",
            theme === 'dark'
              ? "bg-white/10 border-white/20"
              : "bg-white/80 border-gray-200/50"
          )}>
            <h3 className={clsx(
              "text-xl font-bold mb-6 transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Campaigns Created (Last 30 Days)</h3>
            {communicationCampaigns.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={communicationCampaigns}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatChartDate}
                    stroke="#9CA3AF"
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(label: any) => `Date: ${formatTooltipLabel(label)}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Campaigns"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={clsx(
                "text-center py-12 transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>No campaign data available</div>
            )}
          </div>

          {/* Communication Type Breakdown */}
          <div className={clsx(
            "backdrop-blur-lg rounded-xl border p-6 transition-colors",
            theme === 'dark'
              ? "bg-white/10 border-white/20"
              : "bg-white/80 border-gray-200/50"
          )}>
            <h3 className={clsx(
              "text-xl font-bold mb-6 transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Communication by Type</h3>
            {communicationTypeBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={communicationTypeBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, percent }) => `${type}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {communicationTypeBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#10B981', '#3B82F6', '#8B5CF6'][index % 3]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(30, 30, 30, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={clsx(
                "text-center py-12 transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>No communication type data available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;

